import React, { useEffect, useState } from 'react';
import { brokerApi, riskApi } from './api';
import PriceChart from './PriceChart';

function fmtMoney(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toEquitySeries(history) {
  if (!history?.timestamp) return [];
  return history.timestamp
    .map((ts, i) => ({ date: new Date(ts * 1000).toISOString().slice(0, 10), close: history.equity[i] }))
    .filter((p) => p.close != null);
}

function computeDrawdown(equitySeries) {
  let peak = -Infinity;
  let maxDrawdownPct = 0;
  const series = equitySeries.map((p) => {
    peak = Math.max(peak, p.close);
    const drawdownPct = peak > 0 ? ((p.close - peak) / peak) * 100 : 0;
    maxDrawdownPct = Math.min(maxDrawdownPct, drawdownPct);
    return { date: p.date, close: drawdownPct };
  });
  const currentDrawdownPct = series.length ? series[series.length - 1].close : 0;
  return { series, currentDrawdownPct, maxDrawdownPct, peak };
}

function SectorBar({ sector, value, pct }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="summary-item" style={{ marginBottom: 4 }}>
        <span>{sector}</span>
        <span>
          {fmtMoney(value)} · {pct.toFixed(1)}%
        </span>
      </div>
      <div style={{ background: 'var(--surface-2)', borderRadius: 999, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, pct)}%`, background: pct > 35 ? 'var(--amber)' : 'var(--accent)', height: '100%' }} />
      </div>
    </div>
  );
}

function PositionSizeCalculator({ defaultEquity }) {
  const [equity, setEquity] = useState(defaultEquity || 10000);
  const [riskPct, setRiskPct] = useState(1);
  const [entryPrice, setEntryPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');

  useEffect(() => {
    if (defaultEquity) setEquity(defaultEquity);
  }, [defaultEquity]);

  const riskAmount = (Number(equity) || 0) * ((Number(riskPct) || 0) / 100);
  const perShareRisk = Math.abs(Number(entryPrice) - Number(stopPrice));
  const suggestedShares = perShareRisk > 0 ? Math.floor(riskAmount / perShareRisk) : 0;
  const positionValue = suggestedShares * (Number(entryPrice) || 0);
  const positionPct = equity > 0 ? (positionValue / equity) * 100 : 0;

  return (
    <div>
      <div className="form-grid">
        <div className="input-group">
          <label>Account equity</label>
          <input type="number" value={equity} onChange={(e) => setEquity(e.target.value)} min="0" />
        </div>
        <div className="input-group">
          <label>Risk per trade (%)</label>
          <input type="number" value={riskPct} onChange={(e) => setRiskPct(e.target.value)} min="0.1" max="100" step="0.1" />
        </div>
        <div className="input-group">
          <label>Entry price</label>
          <input type="number" value={entryPrice} onChange={(e) => setEntryPrice(e.target.value)} min="0" step="0.01" />
        </div>
        <div className="input-group">
          <label>Stop-loss price</label>
          <input type="number" value={stopPrice} onChange={(e) => setStopPrice(e.target.value)} min="0" step="0.01" />
        </div>
      </div>

      <div className="trade-summary" style={{ marginTop: 16 }}>
        <div className="summary-item">
          <span>Dollar risk at this stop</span>
          <span className="amount">{fmtMoney(riskAmount)}</span>
        </div>
        <div className="summary-item">
          <span>Suggested share count</span>
          <span className="amount">{perShareRisk > 0 ? suggestedShares : '—'}</span>
        </div>
        <div className="summary-item">
          <span>Resulting position size</span>
          <span className="amount">{perShareRisk > 0 ? `${fmtMoney(positionValue)} (${positionPct.toFixed(1)}% of equity)` : '—'}</span>
        </div>
      </div>
      <p className="input-hint" style={{ marginTop: 10 }}>
        This sizes a position so a stop-out loses no more than your chosen risk % of equity. It does not account for
        slippage, gaps, or correlated positions.
      </p>
    </div>
  );
}

export default function RiskDashboard() {
  const [account, setAccount] = useState(null);
  const [equitySeries, setEquitySeries] = useState(null);
  const [sectorData, setSectorData] = useState(null);
  const [sectorError, setSectorError] = useState(null);
  const [patterns, setPatterns] = useState(null);

  useEffect(() => {
    brokerApi.account().then(setAccount).catch(() => {});
    brokerApi
      .accountHistory()
      .then((h) => setEquitySeries(toEquitySeries(h)))
      .catch(() => setEquitySeries([]));
    riskApi
      .sectorExposure()
      .then(setSectorData)
      .catch((err) => setSectorError(err.message));
    riskApi.tradingPatterns().then(setPatterns).catch(() => {});
  }, []);

  const drawdown = equitySeries?.length ? computeDrawdown(equitySeries) : null;

  return (
    <div className="module-container">
      <div className="module-header">
        <h2>Risk Dashboard</h2>
        <p>Sector exposure, drawdown, position sizing, and trading pattern checks based on your real account.</p>
      </div>

      <section className="dashboard-section">
        <h3>Drawdown</h3>
        {!equitySeries ? (
          <span className="skeleton-bar" style={{ width: '100%', height: 200 }} />
        ) : equitySeries.length === 0 ? (
          <div className="empty-state">
            <p>No equity history yet</p>
          </div>
        ) : (
          <>
            <div className="stats-grid" style={{ marginBottom: 14 }}>
              <div className="stat-card">
                <p className="stat-label">Current Drawdown</p>
                <p className={`stat-value ${drawdown.currentDrawdownPct < 0 ? 'down' : ''}`}>
                  {drawdown.currentDrawdownPct.toFixed(2)}%
                </p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Max Drawdown (period)</p>
                <p className="stat-value down">{drawdown.maxDrawdownPct.toFixed(2)}%</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Equity Peak</p>
                <p className="stat-value">{fmtMoney(drawdown.peak)}</p>
              </div>
            </div>
            <PriceChart data={drawdown.series} />
          </>
        )}
      </section>

      <section className="dashboard-section">
        <h3>Sector Exposure</h3>
        {sectorError ? (
          <div className="error-box">{sectorError}</div>
        ) : !sectorData ? (
          <div className="skeleton-list">
            <span className="skeleton-bar" />
          </div>
        ) : sectorData.breakdown.length === 0 ? (
          <div className="empty-state">
            <p>No positions yet</p>
            <small>Sector exposure appears once you hold at least one position</small>
          </div>
        ) : (
          <>
            {sectorData.breakdown.map((s) => (
              <SectorBar key={s.sector} sector={s.sector} value={s.value} pct={s.pct} />
            ))}
            {sectorData.breakdown[0]?.pct > 40 && (
              <div className="error-box" style={{ marginTop: 8 }}>
                {sectorData.breakdown[0].sector} makes up {sectorData.breakdown[0].pct.toFixed(0)}% of your invested
                capital — concentrated sector exposure.
              </div>
            )}
          </>
        )}
      </section>

      <section className="dashboard-section">
        <h3>Trading Pattern Check</h3>
        {!patterns ? (
          <span className="skeleton-bar" style={{ width: '100%' }} />
        ) : (
          <>
            <div className="stats-grid" style={{ marginBottom: 14 }}>
              <div className="stat-card">
                <p className="stat-label">Trades (24h)</p>
                <p className="stat-value">{patterns.tradesLast24h}</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Trades (7d)</p>
                <p className="stat-value">{patterns.tradesLast7d}</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Trades (30d)</p>
                <p className="stat-value">{patterns.tradesLast30d}</p>
              </div>
            </div>
            {patterns.warnings.length === 0 ? (
              <div className="empty-state">
                <p>No pattern warnings</p>
                <small>Trade frequency and reversal patterns look normal</small>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {patterns.warnings.map((w, idx) => (
                  <div key={idx} className={`error-box`} style={{ color: w.level === 'HIGH' ? 'var(--red)' : 'var(--amber)' }}>
                    {w.message}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <section className="dashboard-section">
        <h3>Position Size Calculator</h3>
        <PositionSizeCalculator defaultEquity={account ? Number(account.portfolio_value) : undefined} />
      </section>
    </div>
  );
}
